import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "chunk-reload-attempt";

/**
 * Wraps React.lazy with retry + one-time reload recovery.
 * Fixes "Failed to fetch dynamically imported module" errors caused by
 * transient network failures or stale chunk hashes after a new deploy.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (error) {
      // Second chance: transient network hiccup
      try {
        const mod = await factory();
        sessionStorage.removeItem(RELOAD_KEY);
        return mod;
      } catch (retryError) {
        const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === "true";
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_KEY, "true");
          window.location.reload();
          // Keep suspense pending while the page reloads
          return await new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}
